import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiCpu, FiSmartphone, FiMonitor, FiSettings, FiMessageSquare, 
  FiShield, FiZap, FiLayout, FiEye, FiEyeOff, FiActivity, 
  FiClock, FiExternalLink, FiSave, FiAlertTriangle, FiCheckCircle,
  FiChevronRight, FiMaximize2, FiGlobe
} = FiIcons;
import { mockApps, mockClients, mockVaultSecrets } from '../data/mockData';

const AIAssistant = () => {
  const [selectedAppId, setSelectedAppId] = useState('app_1');
  const [isSaving, setIsSaving] = useState(false);

  // Mock State for Chatbot Config
  const [configs, setConfigs] = useState({
    app_1: {
      enabled: true,
      showDesktop: true,
      showMobile: false,
      provider: 'OpenAI',
      model: 'gpt-4o',
      vaultKeyRef: 'v_sec_2',
      welcomeMessage: 'Hello! I am the BPA Assistant. How can I help you today?',
      systemPrompt: 'You are a helpful assistant for BPA Solutions. You help users navigate the gateway and answer questions about email deliverability.',
      allowedPages: '*, /dashboard, /pricing',
      position: 'bottom-right',
      brandColor: '#2563eb',
      dailyLimit: 100,
      monthlyTokens: 250000,
      fallbackMessage: 'I am currently having trouble connecting. Please try again later.',
      status: 'active',
      lastUpdated: '2 hours ago'
    },
    app_2: {
      enabled: false,
      showDesktop: true,
      showMobile: true,
      provider: 'Gemini',
      model: 'gemini-1.5-pro',
      vaultKeyRef: 'v_sec_2',
      welcomeMessage: 'Welcome to CRM Sync Support.',
      systemPrompt: 'You help with sync errors.',
      allowedPages: '/sync/*',
      position: 'bottom-left',
      brandColor: '#8b5cf6',
      dailyLimit: 50,
      monthlyTokens: 100000,
      fallbackMessage: 'Offline.',
      status: 'paused',
      lastUpdated: '1 day ago'
    }
  });

  const currentApp = mockApps.find(a => a.id === selectedAppId);
  const currentConfig = configs[selectedAppId] || {
    enabled: false,
    provider: 'OpenAI',
    status: 'draft',
    lastUpdated: 'Never'
  };

  const updateConfig = (field, value) => {
    setConfigs({
      ...configs,
      [selectedAppId]: { ...currentConfig, [field]: value }
    });
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert(`AI Configuration saved for ${currentApp.name}`);
    }, 800);
  };

  const mockActivity = [
    { id: 1, action: 'Chatbot enabled for BPA Website', time: '2 hrs ago', user: 'Admin (AD)' },
    { id: 2, action: 'LLM provider changed to OpenAI', time: '5 hrs ago', user: 'Admin (AD)' },
    { id: 3, action: 'System prompt modified', time: '1 day ago', user: 'Manager (JB)' },
    { id: 4, action: 'Daily message limit increased to 100', time: '2 days ago', user: 'Admin (AD)' }
  ];

  const llmSecrets = mockVaultSecrets.filter(s => s.type.includes('LLM'));

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <SafeIcon icon={FiCpu} className="text-blue-600" /> AI Assistant Management
          </h2>
          <p className="text-sm text-slate-500">Configure LLM-powered chatbots and classify incoming requests.</p>
        </div>
        <div className="flex gap-3">
          <select 
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-blue-100 shadow-sm"
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
          >
            {mockApps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
          >
            <SafeIcon icon={isSaving ? FiClock : FiSave} className={isSaving ? 'animate-spin' : ''} />
            {isSaving ? 'Saving...' : 'Save AI Config'}
          </button>
        </div>
      </div>

      {/* Visibility Status Bar */}
      <div className={`p-4 rounded-2xl border flex items-center justify-between transition-all ${currentConfig.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-100 border-slate-200 opacity-80'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentConfig.enabled ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' : 'bg-slate-300 text-slate-500'}`}>
            <SafeIcon icon={currentConfig.enabled ? FiEye : FiEyeOff} className="text-xl" />
          </div>
          <div>
            <h3 className={`font-bold ${currentConfig.enabled ? 'text-emerald-900' : 'text-slate-700'}`}>
              Chatbot {currentConfig.enabled ? 'Visible' : 'Hidden'} on App
            </h3>
            <p className={`text-xs ${currentConfig.enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
              Target: {currentApp.name} • Status: {currentConfig.status.toUpperCase()}
            </p>
          </div>
        </div>
        <button 
          onClick={() => updateConfig('enabled', !currentConfig.enabled)}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${currentConfig.enabled ? 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-slate-900 text-white border-slate-800 hover:bg-slate-800'}`}
        >
          {currentConfig.enabled ? 'Disable Chatbot' : 'Enable Chatbot'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Provider Config */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <SafeIcon icon={FiZap} className="text-blue-500" />
              <h3 className="font-bold text-slate-800">LLM Provider & Model</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Provider</label>
                  <select 
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white"
                    value={currentConfig.provider}
                    onChange={(e) => updateConfig('provider', e.target.value)}
                  >
                    <option>OpenAI</option>
                    <option>Gemini</option>
                    <option>Claude</option>
                    <option>Mistral</option>
                    <option>Other (Custom Endpoint)</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Model Identifier</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono"
                    value={currentConfig.model}
                    onChange={(e) => updateConfig('model', e.target.value)}
                    placeholder="e.g. gpt-4-turbo"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">LLM Key Reference (Vault)</label>
                  <button className="text-[10px] font-bold text-blue-600 hover:underline flex items-center gap-1">
                    <SafeIcon icon={FiExternalLink} /> Open Secure Vault
                  </button>
                </div>
                <div className="p-4 bg-slate-900 rounded-xl border border-slate-800 space-y-3">
                  <select 
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-500"
                    value={currentConfig.vaultKeyRef}
                    onChange={(e) => updateConfig('vaultKeyRef', e.target.value)}
                  >
                    <option value="">Select secure secret reference...</option>
                    {llmSecrets.map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.env})</option>
                    ))}
                  </select>
                  <div className="flex justify-between items-center px-1">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiShield} className="text-emerald-400 text-xs" />
                      <span className="text-[10px] font-mono text-slate-500 tracking-wider">
                        {mockVaultSecrets.find(s => s.id === currentConfig.vaultKeyRef)?.value.substring(0, 12)}••••••••••••••••
                      </span>
                    </div>
                    <span className="text-[9px] text-slate-600 italic">Masked by Security Policy</span>
                  </div>
                </div>
                <p className="text-[10px] text-amber-600 flex items-center gap-1.5 px-1">
                  <SafeIcon icon={FiAlertTriangle} /> Real LLM secrets are never exposed client-side. The gateway uses the reference ID to inject keys in the secure backend.
                </p>
              </div>
            </div>
          </div>

          {/* Behavior & Content */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <SafeIcon icon={FiMessageSquare} className="text-slate-400" />
              <h3 className="font-bold text-slate-800">Persona & Behavior</h3>
            </div>
            <div className="p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Welcome Message</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  value={currentConfig.welcomeMessage}
                  onChange={(e) => updateConfig('welcomeMessage', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">System Prompt (The "Brain")</label>
                <textarea 
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm h-28 resize-none focus:bg-white transition-all"
                  value={currentConfig.systemPrompt}
                  onChange={(e) => updateConfig('systemPrompt', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fallback Message (On Error)</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-italic"
                  value={currentConfig.fallbackMessage}
                  onChange={(e) => updateConfig('fallbackMessage', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Controls */}
        <div className="space-y-6">
          {/* Display & UI */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-6">
            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2 border-b border-slate-50 pb-3">
              <SafeIcon icon={FiLayout} className="text-slate-400" /> UI Configuration
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiMonitor} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-600">Show on Desktop</span>
                </div>
                <button 
                  onClick={() => updateConfig('showDesktop', !currentConfig.showDesktop)}
                  className={`w-10 h-5 rounded-full p-1 transition-all ${currentConfig.showDesktop ? 'bg-blue-600' : 'bg-slate-300'}`}
                >
                  <div className={`w-3 h-3 bg-white rounded-full transition-all transform ${currentConfig.showDesktop ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiSmartphone} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-600">Show on Mobile</span>
                </div>
                <button 
                  onClick={() => updateConfig('showMobile', !currentConfig.showMobile)}
                  className={`w-10 h-5 rounded-full p-1 transition-all ${currentConfig.showMobile ? 'bg-blue-600' : 'bg-slate-300'}`}
                >
                  <div className={`w-3 h-3 bg-white rounded-full transition-all transform ${currentConfig.showMobile ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </button>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-slate-50">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Position</label>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => updateConfig('position', 'bottom-right')}
                    className={`py-2 text-[10px] font-bold rounded-lg border transition-all ${currentConfig.position === 'bottom-right' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200'}`}
                  >Bottom Right</button>
                  <button 
                    onClick={() => updateConfig('position', 'bottom-left')}
                    className={`py-2 text-[10px] font-bold rounded-lg border transition-all ${currentConfig.position === 'bottom-left' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200'}`}
                  >Bottom Left</button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Brand Color</label>
                <div className="flex gap-3 items-center">
                  <input 
                    type="color" 
                    className="w-10 h-10 rounded-lg p-0 bg-transparent border-none cursor-pointer"
                    value={currentConfig.brandColor}
                    onChange={(e) => updateConfig('brandColor', e.target.value)}
                  />
                  <span className="text-xs font-mono font-bold text-slate-600 uppercase">{currentConfig.brandColor}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quotas & Limits */}
          <div className="bg-slate-900 rounded-2xl p-6 text-white space-y-6 shadow-xl">
            <h3 className="font-bold flex items-center gap-2 text-sm">
              <SafeIcon icon={FiActivity} className="text-emerald-400" /> AI Resource Limits
            </h3>
            <div className="space-y-5">
              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] font-bold uppercase text-slate-400">
                  <span>Daily Message Limit</span>
                  <span>{currentConfig.dailyLimit} calls</span>
                </div>
                <input 
                  type="range" min="10" max="1000" step="10"
                  className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  value={currentConfig.dailyLimit}
                  onChange={(e) => updateConfig('dailyLimit', parseInt(e.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] font-bold uppercase text-slate-400">
                  <span>Est. Monthly Tokens</span>
                  <span>{(currentConfig.monthlyTokens / 1000).toLocaleString()}k</span>
                </div>
                <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
                  <p className="text-[9px] text-slate-500 leading-relaxed italic">
                    Token estimates reflect expected annual volume based on current client tier. Overage is currently set to <b>Soft Cap</b>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Log */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 text-sm">Recent Activity</h3>
              <SafeIcon icon={FiClock} className="text-slate-400" />
            </div>
            <div className="p-4 space-y-4">
              {mockActivity.map(item => (
                <div key={item.id} className="relative pl-5 pb-4 border-l border-slate-100 last:pb-0">
                  <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-white"></div>
                  <div className="space-y-0.5">
                    <p className="text-[11px] font-bold text-slate-800">{item.action}</p>
                    <div className="flex justify-between text-[9px] text-slate-400">
                      <span>{item.user}</span>
                      <span>{item.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Preview (Simplified) */}
      {currentConfig.enabled && (
        <div className={`fixed z-40 hidden md:block transition-all duration-500 ${currentConfig.position === 'bottom-right' ? 'bottom-8 right-8' : 'bottom-8 left-8'}`}>
          <div className="bg-white w-72 rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-10">
            <div className="p-4 flex items-center gap-3 border-b border-slate-100" style={{ backgroundColor: currentConfig.brandColor }}>
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white">
                <SafeIcon icon={FiCpu} />
              </div>
              <div className="text-white">
                <h4 className="text-xs font-bold">BPA Assistant</h4>
                <p className="text-[10px] opacity-80">Online & ready</p>
              </div>
            </div>
            <div className="p-4 space-y-3 h-48 bg-slate-50">
              <div className="bg-white p-3 rounded-xl rounded-tl-none shadow-sm text-[11px] text-slate-700 max-w-[90%]">
                {currentConfig.welcomeMessage}
              </div>
            </div>
            <div className="p-3 bg-white border-t border-slate-100 flex gap-2">
              <div className="flex-1 bg-slate-50 rounded-lg px-3 py-1.5 text-[10px] text-slate-400">Type a message...</div>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm" style={{ backgroundColor: currentConfig.brandColor }}>
                <SafeIcon icon={FiMessageSquare} />
              </div>
            </div>
          </div>
          <p className="mt-3 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">Live Config Preview</p>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;